import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button"; // Assuming you have this, or use standard button
import { format } from "date-fns";
import { PackagePlus, RotateCcw, Trash2, FileImage, ExternalLink, Filter, X } from "lucide-react";

const PickLogModal = ({ isOpen, onClose, component, workOrder }) => {
  const [filterLot, setFilterLot] = useState(null);

  if (!isOpen || !component) return null;

  // Aggregate all warehouse entries (picks, returns, waste) from all lots
  const entries = [];
  
  if (component.lots) {
    component.lots.forEach((lot) => {
      // 1. Add Picks
      if (lot.picks) {
        lot.picks.forEach((pick) => {
          entries.push({
            id: pick.pick_id,
            timestamp: pick.picked_at,
            action: "pick",
            lotNumber: lot.lot_number,
            quantity: pick.picked_qty,
            user: pick.picked_by,
            unit: component.unit,
            images: []
          });
        });
      }
      
      // 2. Add Returns
      if (lot.returns) {
        lot.returns.forEach((ret) => {
          entries.push({
            id: ret.return_id,
            timestamp: ret.returned_at,
            action: "return",
            lotNumber: lot.lot_number,
            quantity: ret.returned_qty,
            user: ret.returned_by,
            unit: component.unit,
            reason: ret.reason,
            images: [] // Future proofing
          });
        });
      }

      // 3. Add Waste (Correctly sourced from INSIDE the lot)
      if (lot.waste) {
        lot.waste.forEach((wasteEntry) => {
          entries.push({
            id: wasteEntry.waste_id,
            timestamp: wasteEntry.logged_at,
            action: "waste",
            lotNumber: lot.lot_number,
            quantity: wasteEntry.waste_qty,
            user: wasteEntry.logged_by,
            unit: component.unit,
            reason: wasteEntry.waste_reason,
            images: wasteEntry.waste_images || [] // Capture the S3 URLs
          });
        });
      }
    });
  }
  
  // Sort by timestamp (most recent first)
  entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  // Filter if a specific lot is selected
  const visibleEntries = filterLot 
    ? entries.filter(e => e.lotNumber === filterLot) 
    : entries;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex justify-between items-center mr-8">
            <div>
              <DialogTitle>Warehouse Log - {component.component_name}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-gray-500">History & Traceability</p>
                {filterLot && (
                  <Badge variant="secondary" className="flex gap-1 items-center cursor-pointer hover:bg-gray-200" onClick={() => setFilterLot(null)}>
                    Filter: {filterLot} <X className="w-3 h-3" />
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {visibleEntries.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                <tr className="border-b text-gray-600">
                  <th className="text-left p-3 font-semibold w-32">Date/Time</th>
                  <th className="text-left p-3 font-semibold w-24">Action</th>
                  <th className="text-left p-3 font-semibold w-32">
                    <div className="flex items-center gap-1">
                      Lot Number
                      {!filterLot && <Filter className="w-3 h-3 text-gray-400" />}
                    </div>
                  </th>
                  <th className="text-right p-3 font-semibold w-24">Quantity</th>
                  <th className="text-center p-3 font-semibold w-16">Evidence</th>
                  <th className="text-left p-3 font-semibold">Notes / Reason</th>
                  <th className="text-left p-3 font-semibold w-24">User</th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map((entry, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-3 text-gray-600 whitespace-nowrap">
                      {entry.timestamp ? format(new Date(entry.timestamp), 'MMM dd, HH:mm') : 'N/A'}
                    </td>
                    <td className="p-3">
                      <Badge 
                        variant="outline"
                        className={`flex w-fit items-center gap-1
                          ${entry.action === 'pick' ? 'bg-green-50 text-green-700 border-green-200' :
                            entry.action === 'return' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                            'bg-red-50 text-red-700 border-red-200'
                          }`}
                      >
                        {entry.action === 'pick' && <PackagePlus className="w-3 h-3" />}
                        {entry.action === 'return' && <RotateCcw className="w-3 h-3" />}
                        {entry.action === 'waste' && <Trash2 className="w-3 h-3" />}
                        {entry.action.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <button 
                        onClick={() => setFilterLot(filterLot === entry.lotNumber ? null : entry.lotNumber)}
                        className={`font-mono text-xs px-1.5 py-0.5 rounded border transition-colors
                          ${filterLot === entry.lotNumber 
                            ? 'bg-blue-100 text-blue-700 border-blue-300 font-bold' 
                            : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
                          }`}
                        title="Filter history by this Lot"
                      >
                        {entry.lotNumber}
                      </button>
                    </td>
                    <td className="p-3 text-right font-medium">
                      {entry.quantity.toFixed(2)} <span className="text-xs text-gray-500">{entry.unit}</span>
                    </td>
                    <td className="p-3 text-center">
                      {entry.images && entry.images.length > 0 ? (
                        <div className="flex justify-center gap-1">
                          {entry.images.map((imgUrl, i) => (
                            <a 
                              key={i} 
                              href={imgUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                              title="View Evidence"
                            >
                              {entry.images.length === 1 ? (
                                <span className="flex items-center gap-1 text-xs underline">
                                  <FileImage className="w-4 h-4" /> View
                                </span>
                              ) : (
                                <FileImage className="w-4 h-4" />
                              )}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">-</span>
                      )}
                    </td>
                    <td className="p-3 text-gray-600 text-sm">
                      {entry.reason && (
                        <span className="block font-medium text-xs text-gray-800 mb-0.5">
                          {entry.reason.replace(/_/g, ' ')}
                        </span>
                      )}
                      {/* Truncate long notes */}
                      <span className="text-gray-500 text-xs" title={entry.notes}>
                        {entry.notes ? (entry.notes.length > 30 ? entry.notes.substring(0, 30) + '...' : entry.notes) : ''}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-gray-500">
                      {entry.user || 'System'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Filter className="w-12 h-12 mb-2 opacity-20" />
              <p>No records found</p>
              {filterLot && (
                <Button variant="link" onClick={() => setFilterLot(null)} className="mt-2">
                  Clear Filter
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PickLogModal;
